import type { MessageSecurity, SecurityAction } from './messageSecurity';
/**
 * Grievance Desk — typed API wrappers (Task F1).
 *
 * Re-declares the shared contract types verbatim from the plan's "API
 * contract" section (track-F-brief.md) so this package does not depend on
 * the backend worktree. Every function unwraps `res.data.data` (the app's
 * `{ success, data }` envelope) and returns the typed payload.
 *
 * The backend does not exist in this worktree — these wrappers are pure
 * HTTP calls against the contract's paths; they are exercised in tests via
 * a mocked `api` client, never a live server.
 */
import { api } from '../../utils/api';

/* ── Shared contract types (verbatim from the plan) ──────────────────── */

export type CaseStatus = 'NEW' | 'ASSIGNED' | 'AWAITING_APPROVAL' | 'LSP_REPLIED' | 'WAITING_BORROWER' | 'RESOLVED_BY_LSP' | 'CLOSED' | 'JUNK';
export type SlaState = 'OK' | 'ATTENTION' | 'PAST_DUE' | 'REPLIED' | 'CLOSED';
export type MessageKind = 'INBOUND' | 'OUTBOUND' | 'ACK' | 'NOTE' | 'DRAFT';
/** Approval step (2026-09-22): only partner-authored DRAFT rows carry one. */
export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUPERSEDED';
export type NoteVisibility = 'INTERNAL' | 'SHARED';
export type Category = 'LOAN_CLOSURE_NOC' | 'RECOVERY_CONDUCT' | 'CHARGES_FEES' | 'CREDIT_REPORT' | 'PAYMENT_NOT_UPDATED' | 'OTHER';

export interface SlaInfo {
  state: SlaState;
  replyDueAt: string | null;
  workingMinutesLeft: number | null;
  rbiDeadlineAt: string;
}

export interface CaseListItem {
  id: string;
  ref: string;
  subject: string;
  status: CaseStatus;
  borrowerName: string | null;
  borrowerEmail: string;
  loanNumber: string | null;
  partner: { id: string; name: string } | null;
  suggestedPartner: { id: string; name: string } | null;
  receivedAt: string;
  ackSentAt: string | null;
  sla: SlaInfo;
  /** Approval step: the case's PENDING partner draft, if any. Optional in the
   * type so pre-approval fixtures still compile; the server always sends it. */
  pendingDraft?: { messageId: string; partnerName: string; createdAt: string } | null;
  /** 2026-09-28: latest answer + feedback cycle. Partner view: delivery/feedback/rating/comment are always null. */
  answered?: AnsweredInfo | null;
}

export type FeedbackState = 'not_answered' | 'waiting' | 'sent' | 'rated' | 'unhappy' | 'bounced';
export interface AnsweredInfo {
  at: string;
  side: 'PARTNER' | 'NISHTHA';
  partner: { id: string; name: string } | null;
  /** 2026-10-13: true = the latest reply was the FINAL answer (feedback mail + auto-close armed); false = holding reply. */
  final?: boolean;
  /** MailKart status of the answer: DELIVERED, OPEN, CLICK, HARD_BOUNCE, … (staff only). */
  delivery: string | null;
  feedback: FeedbackState | null;
  feedbackDueAt: string | null;
  feedbackSentAt: string | null;
  autoCloseAt: string | null;
  keepOpen: boolean;
  rating: number | null;
  comment: string | null;
}

export interface AttachmentMeta { id: string; filename: string; size: number; contentType?: string; contentId?: string | null; isInline?: boolean }
export interface ReplyAttachment { filename: string; content: string; contentType?: string }

export interface CaseMessage {
  id: string;
  kind: MessageKind;
  visibility: 'PUBLIC' | NoteVisibility;
  fromAddress: string | null;
  fromName: string | null;
  toAddresses: string[];
  authorName: string | null;
  authorSide: 'BORROWER' | 'NISHTHA' | 'PARTNER' | 'SYSTEM';
  subject: string | null;
  text: string;
  html: string | null;
  attachments: AttachmentMeta[];
  createdAt: string;
  /** UNKNOWN (fix round 1 / I3): an approve got no answer from MailKart — it may have gone out. */
  sendStatus: 'SENT' | 'FAILED' | 'UNKNOWN' | null;
  /* Approval step (2026-09-22) — null for non-draft rows. A DRAFT that was
   * approved comes back as kind 'OUTBOUND' with approvalStatus 'APPROVED'.
   * Optional in the type so pre-approval fixtures compile; the server always
   * sends them. */
  approvalStatus?: ApprovalStatus | null;
  reviewNote?: string | null;
  originalText?: string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  reviewedAt?: string | null;
  /** Who sent the draft back (REJECTED). */
  reviewedByName?: string | null;
  /** The partner the row belongs to (drafts: its author's partner). A partner login only gets its own. */
  partnerName?: string | null;
  /** 2026-10-13: OUTBOUND/DRAFT marked "final answer" (only these carry the RBI footer and arm feedback). */
  isFinal?: boolean;
}

export interface CaseEvent {
  id: string;
  type: string;
  text: string;
  actorName: string | null;
  createdAt: string;
}

export interface CaseDetail extends CaseListItem {
  category: Category | null;
  priority: 'NORMAL' | 'HIGH';
  borrowerPhone: string | null;
  match: { partnerId: string | null; loanNumber: string | null; confidence: 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE'; reasons: string[] };
  assignedMember: { id: string; name: string } | null;
  /** Latest partner "Mark resolved" summary; staff edit it before the closing mail (fix round 1 / I5). */
  resolveSummary: string | null;
  messages: CaseMessage[];
  events: CaseEvent[];
}

export interface PartnerSummary {
  id: string;
  name: string;
  isActive: boolean;
  open: number;
  needsAttention: number;
  pastDue: number;
  closed30d: number;
  avgFirstReplyWorkingMinutes: number | null;
  onTimePct: number | null;
  logins: number;
  rules: number;
  /** 2026-09-28 (staff): open cases this side answered; ratings in the last 30 days. */
  answered?: number;
  ratingAvg?: number | null;
  ratingCount?: number;
  unhappy?: number;
}

export interface DeskDashboard {
  kpis: {
    newUnassigned: number;
    unmatched: number;
    openWithPartners: number;
    needsAttention: number;
    pastDue: number;
    closed30d: number;
    avgFirstReplyWorkingMinutes: number | null;
    /** Approval step: cases with a PENDING partner draft. */
    awaitingApproval: number;
    /** Real (wall-clock) minutes the oldest pending draft has waited. */
    oldestApprovalWaitMinutes: number | null;
    /** 2026-09-28 answered + feedback. */
    answered?: number;
    feedbackSent?: number;
    feedbackWaiting?: number;
    ratingAvg?: number | null;
    ratingCount?: number;
    unhappy?: number;
    /** 2026-09-29 (V1 tiles). */
    replyDrafted?: number;
    replyDraftedApproved?: number;
    sentBackWaiting?: number;
    sentBackTotal?: number;
    /** 2026-11-04: cases marked JUNK (all time). */
    junk?: number;
  };
  partners: PartnerSummary[];
  /** 2026-09-29: count per inbox tab for the current top filter. */
  tabCounts?: Partial<Record<CaseFilter, number>>;
  /** The LSP the tiles are scoped to (partner id / 'NISHTHA'), or null. */
  scope?: string | null;
  /** `skipped` = mail the sync gave up on, still open (fix round 2 / N1); keeps `ok` false. */
  sync: { mailbox: string; lastSyncAt: string | null; ok: boolean; message: string | null; skipped: number };
  officeOpen: boolean;
}

/** A MailKart message the scheduled sync gave up on (fix round 2 / N1). */
export interface SyncSkip {
  id: string;
  mailkartMessageId: string;
  fromAddress: string | null;
  subject: string | null;
  error: string;
  attempts: number;
  skippedAt: string;
}

/** 2026-10-08: lsp_developer is a partner login for Developer Access only (no grievance cases). */
export type PartnerRole = 'lsp_manager' | 'lsp_agent' | 'lsp_developer';

export interface PartnerMember {
  userId: string;
  name: string;
  email: string;
  role: PartnerRole;
  isActive: boolean;
  lastLoginAt: string | null;
}

export interface PartnerDetail {
  id: string;
  name: string;
  isActive: boolean;
  grievanceOfficerName: string | null;
  escalationEmail: string | null;
  notifyEmails: string[];
  loanNumberPrefixes: string[];
  keywords: string[];
  members: PartnerMember[];
  /** A17 (2026-10-08, RBI checklist): a gateway key needs all three, with nextReviewAt in the future. */
  contractRef?: string | null;
  dueDiligenceAt?: string | null;
  nextReviewAt?: string | null;
}

export interface GrievanceTemplate {
  subject: string;
  body: string;
}

/** `final_reply_footer` (2026-10-13): the RBI escalation paragraph appended to every final reply — body only, no subject. */
export type TemplateKey = 'ack' | 'again' | 'closed' | 'lsp_assigned' | 'lsp_reminder' | 'lsp_past_due' | 'feedback' | 'final_reply_footer';

export interface GrievanceSettingsDto {
  mailboxAddress: string;
  ackEnabled: boolean;
  workDays: number[];
  workStartMinute: number;
  workEndMinute: number;
  holidays: string[];
  slaWorkingMinutes: number;
  attentionWorkingMinutes: number;
  groName: string | null;
  groPhone: string | null;
  nishthaAlertEmails: string[];
  templates: Record<TemplateKey, GrievanceTemplate>;
  syncStartAt: string;
  lastSyncAt: string | null;
  /** 2026-09-28 feedback mail + auto-close (clock minutes). */
  feedbackEnabled?: boolean;
  feedbackDelayMinutes?: number;
  autoCloseMinutes?: number;
}

/** `junk` (2026-11-04): the only filter that lists JUNK cases. */
export type CaseFilter = 'all' | 'unassigned' | 'attention' | 'approval' | 'with_partner' | 'closed' | 'junk';
export type PartnerCaseFilter = 'attention' | 'open' | 'approval' | 'waiting' | 'answered' | 'closed';
/** Staff-only filters that work inside every desk tab (2026-09-28). */
export type AnsweredByFilter = 'any' | 'NISHTHA' | string;
export type FeedbackFilter = 'any' | FeedbackState;

/* ── Approval step (2026-09-22) ─────────────────────────────────────── */

export type ApprovalQueueStatus = 'pending' | 'rejected' | 'approved_today';

export interface ApprovalQueueItem {
  caseId: string;
  ref: string;
  subject: string;
  borrowerName: string | null;
  borrowerEmail: string;
  loanNumber: string | null;
  category: Category | null;
  partner: { id: string; name: string };
  sla: SlaInfo;
  draft: {
    messageId: string;
    text: string;
    authorName: string | null;
    createdAt: string;
    attachments: AttachmentMeta[];
    /** The partner's text when staff edited it before approving, else null. */
    originalText: string | null;
    /** 2026-10-13: the LSP's "final answer" tick; staff may change it at approval. */
    final?: boolean;
  };
  /** Latest INBOUND message. */
  complaint: { messageId?: string; text: string; html?: string | null; createdAt: string; attachments: AttachmentMeta[] };
  /** REJECTED: reviewNote + reviewedAt + reviewedByName. APPROVED: approvedAt + approvedByName.
   * Shape pinned by __tests__/fixtures/approval-queue.contract.json (written from the backend). */
  reviewNote: string | null;
  reviewedAt: string | null;
  reviewedByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
}

export interface ApprovalQueue {
  items: ApprovalQueueItem[];
  counts: { pending: number; rejected: number; approvedToday: number };
}

export interface PartnerDashboard {
  /** 2026-09-28: this partner's cases answered and waiting on the borrower. */
  answered?: number;
  open: number;
  needsAttention: number;
  pastDue: number;
  closed30d: number;
  onTimePct: number | null;
  waitingBorrower: number;
  /** Approval step: this partner's cases with a pending draft. */
  waitingApproval: number;
}

export interface ListParams {
  filter?: CaseFilter;
  q?: string;
  page?: number;
  pageSize?: number;
  answeredBy?: AnsweredByFilter;
  feedback?: FeedbackFilter;
  /** 2026-09-29: 'drafted' = LSP wrote a draft · 'sent_back' = newest draft sent back, not redone. */
  work?: 'drafted' | 'sent_back';
}

export interface PartnerListParams {
  filter?: PartnerCaseFilter;
  page?: number;
  pageSize?: number;
}

export interface AssignInput {
  partnerId: string | null;
  category: Category;
  priority: 'NORMAL' | 'HIGH';
  notifyPartner: boolean;
}

export interface MatchInput {
  loanNumber: string | null;
  borrowerName: string | null;
  borrowerPhone: string | null;
}

export interface CreatePartnerInput {
  name: string;
  grievanceOfficerName?: string;
  escalationEmail?: string;
  notifyEmails?: string[];
  loanNumberPrefixes?: string[];
  keywords?: string[];
}

export interface UpdatePartnerInput extends Partial<CreatePartnerInput> {
  isActive?: boolean;
  /** A17 — ISO instants (or null to clear). */
  contractRef?: string | null;
  dueDiligenceAt?: string | null;
  nextReviewAt?: string | null;
}

export interface AddLoginInput {
  firstName: string;
  lastName: string;
  email: string;
  role: PartnerRole;
  password: string;
}

async function unwrap<T>(promise: Promise<{ data: { data: T } }>): Promise<T> {
  const res = await promise;
  return res.data.data;
}

/* ── Staff API ─────────────────────────────────────────────────────── */

export const staffApi = {
  security: (caseId:string,messageId:string) => unwrap<MessageSecurity>(api.get(`/grievances/cases/${encodeURIComponent(caseId)}/messages/${encodeURIComponent(messageId)}/security`)),
  securityAction: (caseId:string,messageId:string,action:SecurityAction) => unwrap<MessageSecurity>(api.post(`/grievances/cases/${encodeURIComponent(caseId)}/messages/${encodeURIComponent(messageId)}/security`,action)),
  // 2026-09-29 filter-on-top: tiles + tab counts follow the chosen LSP (and tab counts the feedback filter).
  dashboard: (params: { answeredBy?: string; feedback?: string; work?: string } = {}) => unwrap<DeskDashboard>(api.get('/grievances/dashboard', { params })),

  listCases: (params: ListParams = {}) =>
    unwrap<{ items: CaseListItem[]; total: number }>(api.get('/grievances/cases', { params })),

  getCase: (id: string) => unwrap<CaseDetail>(api.get(`/grievances/cases/${id}`)),

  /** `final` (2026-10-13): "This is the final answer" — starts the feedback mail + auto-close. Default (false) = holding reply. */
  reply: (id: string, text: string, attachments?: ReplyAttachment[], final = false) =>
    unwrap<CaseDetail>(api.post(`/grievances/cases/${id}/reply`, { text, ...(attachments?.length ? { attachments } : {}), final })),

  addNote: (id: string, text: string, visibility: NoteVisibility) =>
    unwrap<CaseDetail>(api.post(`/grievances/cases/${id}/notes`, { text, visibility })),

  assign: (id: string, input: AssignInput) => unwrap<CaseDetail>(api.post(`/grievances/cases/${id}/assign`, input)),

  setMatch: (id: string, input: MatchInput) => unwrap<CaseDetail>(api.post(`/grievances/cases/${id}/match`, input)),

  close: (id: string, sendClosingMail: boolean, summary?: string) =>
    unwrap<CaseDetail>(api.post(`/grievances/cases/${id}/close`, summary === undefined ? { sendClosingMail } : { sendClosingMail, summary })),

  reopen: (id: string) => unwrap<CaseDetail>(api.post(`/grievances/cases/${id}/reopen`)),

  // 2026-11-04 junk: not a complaint (auto-replies, test mail). `reason` is optional.
  junk: (id: string, reason?: string) =>
    unwrap<CaseDetail>(api.post(`/grievances/cases/${id}/junk`, reason?.trim() ? { reason: reason.trim() } : {})),
  unjunk: (id: string) => unwrap<CaseDetail>(api.post(`/grievances/cases/${id}/unjunk`)),

  // 2026-09-28 after-reply strip.
  sendFeedbackNow: (id: string) => unwrap<CaseDetail>(api.post(`/grievances/cases/${id}/feedback/send-now`)),
  setKeepOpen: (id: string, keepOpen: boolean) => unwrap<CaseDetail>(api.post(`/grievances/cases/${id}/keep-open`, { keepOpen })),

  partners: () => unwrap<PartnerSummary[]>(api.get('/grievances/partners')),

  partner: (id: string) => unwrap<PartnerDetail>(api.get(`/grievances/partners/${id}`)),

  createPartner: (input: CreatePartnerInput) => unwrap<PartnerDetail>(api.post('/grievances/partners', input)),

  updatePartner: (id: string, input: UpdatePartnerInput) =>
    unwrap<PartnerDetail>(api.patch(`/grievances/partners/${id}`, input)),

  addLogin: (id: string, input: AddLoginInput) =>
    unwrap<PartnerDetail>(api.post(`/grievances/partners/${id}/logins`, input)),

  setLoginActive: (id: string, userId: string, isActive: boolean) =>
    unwrap<PartnerDetail>(api.patch(`/grievances/partners/${id}/logins/${userId}`, { isActive })),

  settings: () => unwrap<GrievanceSettingsDto>(api.get('/grievances/settings')),

  saveSettings: (input: Omit<GrievanceSettingsDto, 'mailboxAddress' | 'syncStartAt' | 'lastSyncAt'>) =>
    unwrap<GrievanceSettingsDto>(api.put('/grievances/settings', input)),

  testAck: () => unwrap<{ sent: true }>(api.post('/grievances/settings/test-ack')),

  syncNow: () => unwrap<{ imported: number; created: number }>(api.post('/grievances/sync/run')),

  listSkips: () => unwrap<SyncSkip[]>(api.get('/grievances/sync/skips')),
  retrySkip: (id: string) =>
    unwrap<{ result: 'skipped' | 'created' | 'appended' }>(api.post(`/grievances/sync/skips/${id}/retry`)),
  dismissSkip: (id: string) => unwrap<{ dismissed: true }>(api.post(`/grievances/sync/skips/${id}/dismiss`)),

  attachmentUrl: (caseId: string, attachmentId: string) =>
    `/api/v1/grievances/cases/${caseId}/attachments/${attachmentId}`,

  /* Approval step (2026-09-22) */
  approvals: (status: ApprovalQueueStatus = 'pending') =>
    unwrap<ApprovalQueue>(api.get('/grievances/approvals', { params: { status } })),

  /** `text` only when staff edited the draft — omitted means "send as written". `final` (2026-10-13) = Nishtha's decision at approval. */
  approveDraft: (caseId: string, messageId: string, text?: string, final?: boolean) =>
    unwrap<CaseDetail>(
      api.post(`/grievances/cases/${caseId}/drafts/${messageId}/approve`, { ...(text === undefined ? {} : { text }), ...(final === undefined ? {} : { final }) }),
    ),

  sendBackDraft: (caseId: string, messageId: string, note: string) =>
    unwrap<CaseDetail>(api.post(`/grievances/cases/${caseId}/drafts/${messageId}/send-back`, { note })),
};

/* ── Partner (LSP) API ────────────────────────────────────────────── */

export const partnerApi = {
  security: (caseId:string,messageId:string) => unwrap<MessageSecurity>(api.get(`/partner/grievances/cases/${encodeURIComponent(caseId)}/messages/${encodeURIComponent(messageId)}/security`)),
  securityAction: (caseId:string,messageId:string,action:SecurityAction) => unwrap<MessageSecurity>(api.post(`/partner/grievances/cases/${encodeURIComponent(caseId)}/messages/${encodeURIComponent(messageId)}/security`,action)),
  me: () => unwrap<{ partner: { id: string; name: string }; role: 'lsp_manager' | 'lsp_agent'; name: string }>(
    api.get('/partner/grievances/me'),
  ),

  dashboard: () => unwrap<PartnerDashboard>(api.get('/partner/grievances/dashboard')),

  listCases: (params: PartnerListParams = {}) =>
    unwrap<{ items: CaseListItem[]; total: number }>(api.get('/partner/grievances/cases', { params })),

  getCase: (id: string) => unwrap<CaseDetail>(api.get(`/partner/grievances/cases/${id}`)),

  /** Approval step: creates a DRAFT for Nishtha to approve — nothing is sent. `final` = the LSP's tick (Nishtha decides at approval). */
  reply: (id: string, text: string, attachments?: ReplyAttachment[], final = false) =>
    unwrap<CaseDetail>(api.post(`/partner/grievances/cases/${id}/reply`, { text, ...(attachments?.length ? { attachments } : {}), final })),

  note: (id: string, text: string) => unwrap<CaseDetail>(api.post(`/partner/grievances/cases/${id}/notes`, { text })),

  resolve: (id: string, summary: string) =>
    unwrap<CaseDetail>(api.post(`/partner/grievances/cases/${id}/resolve`, { summary })),

  sendBack: (id: string, reason: string) =>
    unwrap<CaseDetail>(api.post(`/partner/grievances/cases/${id}/send-back`, { reason })),

  handTo: (id: string, userId: string | null) =>
    unwrap<CaseDetail>(api.post(`/partner/grievances/cases/${id}/hand-to`, { userId })),

  members: () => unwrap<{ id: string; name: string }[]>(api.get('/partner/grievances/members')),

  attachmentUrl: (caseId: string, attachmentId: string) =>
    `/api/v1/partner/grievances/cases/${caseId}/attachments/${attachmentId}`,
};
